import type { App } from 'vue';

/**
 * This is helper function to register plugins like a nuxt
 * To register a plugin just export a const function `defineVuePlugin` that takes `app` as argument and call `app.use`
 * For Scanning plugins it will include all files in `src/plugins` and `src/plugins/**\/index.ts`
 *
 *
 * @param {App} app Vue app instance
 * @returns void
 *
 * @example
 * ```ts
 * // File: src/plugins/vuetify/index.ts
 *
 * import type { App } from 'vue'
 * import { createVuetify } from 'vuetify'
 *
 * const vuetify = createVuetify({ ... })
 *
 * export default function (app: App) {
 *   app.use(vuetify)
 * }
 * ```
 *
 * All you have to do is use this helper function in `main.ts` file like below:
 * ```ts
 * // File: src/main.ts
 * import { registerPlugins } from '@core/utils/plugins'
 * import { createApp } from 'vue'
 * import App from '@/App.vue'
 *
 * // Create vue app
 * const app = createApp(App)
 *
 * // Register plugins
 * registerPlugins(app) // [!code focus]
 *
 * // Mount vue app
 * app.mount('#app')
 * ```
 */

// 등록된 플러그인을 추적하기 위한 Set
const registeredPlugins = new Set<string>();

// 플러그인 등록 순서 정의 (pinia -> router -> 나머지)
const PLUGIN_PRIORITY = {
  'pinia': 1,  // 참고: 파일 이름 기반
  'router': 2, // router 디렉토리 / index.ts
  'default': 100,
};

export const registerPlugins = (app: App) => {
  const imports = import.meta.glob<{ default: (app: App) => void }>(['../../plugins/*.{ts,js}', '../../plugins/*/index.{ts,js}'], { eager: true });

  const importPaths = Object.keys(imports).sort();

  // 플러그인 순서를 정렬
  const sortedPaths = [...importPaths].sort((a, b) => {
    // 이름과 경로에서 플러그인 이름 추출
    const getPluginName = (path: string) => {
      const parts = path.split('/');
      if (parts[parts.length - 2] === 'plugins') {
        // plugins/*.ts 형태
        return parts[parts.length - 1].split('.')[0];
      } else {
        // plugins/*/index.ts 형태
        return parts[parts.length - 2];
      }
    };
    
    const aName = getPluginName(a);
    const bName = getPluginName(b);
    
    // 정의된 순서대로 정렬
    const aPriority = PLUGIN_PRIORITY[aName] || PLUGIN_PRIORITY.default;
    const bPriority = PLUGIN_PRIORITY[bName] || PLUGIN_PRIORITY.default;
    
    return aPriority - bPriority;
  });

  //console.log('Plugin registration order:', sortedPaths);

  sortedPaths.forEach(path => {
    // 플러그인의 이름 추출 (경로에서 파일 이름 부분만)
    const pathParts = path.split('/');
    let pluginName: string;
    
    if (pathParts[pathParts.length - 2] === 'plugins') {
      // plugins/*.ts 형태
      pluginName = pathParts[pathParts.length - 1].split('.')[0];
    } else {
      // plugins/*/index.ts 형태
      pluginName = pathParts[pathParts.length - 2];
    }
    
    // 이미 등록된 플러그인은 건너띀
    if (registeredPlugins.has(pluginName)) {
      // console.log(`Plugin ${pluginName} already registered, skipping`);
      return;
    }
    
    const pluginImportModule = imports[path];
    
    // 플러그인 등록
    if (pluginImportModule.default) {
      try {
        pluginImportModule.default(app);
        registeredPlugins.add(pluginName);
        // console.log(`Registered plugin: ${pluginName}`);
      } catch (error) {
        console.error(`Error registering plugin ${pluginName}:`, error);
      }
    }
  });

};
